import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { io } from "socket.io-client";
import { apiUrl } from "../config/api";
import { clearSyncTimestamp, getStoredSyncTimestamp, saveSyncTimestamp } from "../helper/utilsFunctions";

// Backend stale-cleanup fires every 5 min and drops sockets silent for >15 min.
// We ping every 5 min to keep lastPing fresh in connectionStates.
const CUSTOM_PING_INTERVAL_MS = 5 * 60 * 1000;

export const useSocketInit = (handlers) => {
    const userId = useSelector((state) => state.user.userData?.userId);
    const socketRef = useRef(null);
    const [isConnected, setIsConnected] = useState(false);
    const ackQueueRef = useRef([]);
    const ackTimerRef = useRef(null);
    const pingTimerRef = useRef(null);

    // Keep a live ref to handlers so socket listeners always invoke the latest version
    // without needing to re-register on every render.
    const handlersRef = useRef(handlers);
    useEffect(() => {
        handlersRef.current = handlers;
    });

    const flushAckQueue = useCallback(() => {
        const socket = socketRef.current;
        const queue = ackQueueRef.current;

        if (!socket || !socket.connected || queue.length === 0) return;

        const uniqueIds = [...new Set(queue)];
        ackQueueRef.current = [];

        socket.emit("messagesAcked", {
            messageIds: uniqueIds,
            syncTimestamp: getStoredSyncTimestamp(),
        }, (resp) => {
            if (!resp || resp.status !== "success") return;
            saveSyncTimestamp(resp.syncTimestamp);
        });
    }, []);

    const runInitialSync = useCallback(() => {
        const socket = socketRef.current;
        if (!socket) return;

        socket.emit("requestSync", { lastSyncTimestamp: getStoredSyncTimestamp() }, (resp) => {
            if (!resp || resp.status !== "success") return;

            if (resp.reason === "INVALID_TIMESTAMP") {
                clearSyncTimestamp();
            }

            saveSyncTimestamp(resp.syncTimestamp);
        });
    }, []);

    const queueAck = useCallback((messageId) => {
        if (!messageId) return;

        ackQueueRef.current.push(messageId);

        if (ackQueueRef.current.length >= 30) {
            flushAckQueue();
        }
    }, [flushAckQueue]);

    const disconnectSocket = useCallback(() => {
        flushAckQueue();

        if (pingTimerRef.current) {
            clearInterval(pingTimerRef.current);
            pingTimerRef.current = null;
        }

        if (socketRef.current) {
            socketRef.current.disconnect();
            socketRef.current.removeAllListeners();
            socketRef.current = null;
        }
    }, [flushAckQueue]);

    const connectSocket = useCallback(() => {
        if (!userId || socketRef.current) return;

        socketRef.current = io(apiUrl, {
            transports: ['websocket'],
            reconnection: true,
            reconnectionAttempts: Infinity,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            // Auth as a function so lastSyncTimestamp is read fresh on every
            // connect/reconnect attempt, not frozen at the initial io() call.
            auth: (cb) => cb({
                userId,
                deviceType: "web",
                lastSyncTimestamp: getStoredSyncTimestamp(),
            }),
        });

        socketRef.current.on('connect', () => {
            setIsConnected(true);
            runInitialSync();
            console.log('Socket connected:', socketRef.current?.id);
        });

        socketRef.current.on('disconnect', (reason) => {
            setIsConnected(false);
            console.log('Socket disconnected:', reason);
        });

        socketRef.current.on("connect_error", () => {
            setIsConnected(false);
        });

        // All listeners go through handlersRef so they always call the latest
        // handler version even if SocketProvider re-renders between events.
        socketRef.current.on('newMessage', (payload) => {
            handlersRef.current.onNewMessage(payload, socketRef.current);
            if (payload?.message?.messageId) {
                queueAck(payload.message.messageId);
            }
        });
        socketRef.current.on('messageDelivered', (payload) => handlersRef.current.onDelivered(payload));
        socketRef.current.on('messageRead', (payload) => handlersRef.current.onRead(payload));
        socketRef.current.on('reactedMessage', (payload) => handlersRef.current.onReaction(payload));
        socketRef.current.on('userTyping', (payload) => handlersRef.current.onTyping(payload));
        socketRef.current.on('deletedMessage', (payload) => handlersRef.current.onDelete(payload));
        socketRef.current.on('storyViewed', (payload) => handlersRef.current.onStoryView(payload));

        // Custom heartbeat to keep lastPing fresh in backend connectionStates.
        pingTimerRef.current = setInterval(() => {
            if (socketRef.current?.connected) {
                socketRef.current.emit('ping');
            }
        }, CUSTOM_PING_INTERVAL_MS);

    }, [userId, runInitialSync, queueAck]);

    useEffect(() => {
        ackTimerRef.current = setInterval(flushAckQueue, 5000);

        return () => {
            if (ackTimerRef.current) clearInterval(ackTimerRef.current);
        };
    }, [flushAckQueue]);

    useEffect(() => {
        connectSocket();

        return () => disconnectSocket();
    }, [userId, connectSocket, disconnectSocket]);

    return { socket: socketRef.current, isConnected, queueAck };
};
